import { getToken } from "../../utils/auth";

// Client of api/crm-postventa-ipc. Same session contract as Rollo (crmPostventaRolloApi.js): the token travels in
// the X-CRM-Token header and in the body of POSTs; errors throw an Error with the server "mensaje" and the HTTP
// status (400, 403, 404, 409).
const API_ROOT = "/api/crm-postventa-ipc";

const withTokenHeader = (token) => ({
  "Content-Type": "application/json",
  "X-CRM-Token": token || getToken() || "",
});

const handleResponse = async (response) => {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.mensaje || payload?.message || "No se pudo completar la operación.");
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
};

// Empty values are dropped; false and 0 are kept (incluirHistorico=false, responsableUsuarioId=0 "Sin asignar").
const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, value);
    }
  });

  const serialized = query.toString();
  return serialized ? `?${serialized}` : "";
};

const get = (path, params) => {
  const token = getToken() || "";
  return fetch(`${API_ROOT}${path}${buildQuery(params)}`, {
    method: "GET",
    headers: withTokenHeader(token),
  });
};

const getJson = async (path, params) => handleResponse(await get(path, params));

const postJson = async (path, body = {}) => {
  const token = getToken() || "";
  const response = await fetch(`${API_ROOT}${path}`, {
    method: "POST",
    headers: withTokenHeader(token),
    body: JSON.stringify({
      token,
      ...body,
    }),
  });

  return handleResponse(response);
};

// File name of a Content-Disposition header (RFC 5987 filename* first, then filename), or null.
const fileNameOf = (disposition) => {
  const value = String(disposition || "");
  const encoded = /filename\*\s*=\s*(?:UTF-8|utf-8)?''([^;]+)/.exec(value);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ""));
    } catch (decodeError) {
      // Malformed encoding: fall back to the plain filename.
    }
  }
  const plain = /filename\s*=\s*("([^"]+)"|[^;]+)/.exec(value);
  const name = plain ? (plain[2] || plain[1]).trim() : "";
  return name || null;
};

export const crmPostventaIpcApi = {
  // GET gestiones?vista=activas|finalizadas&seccion=todas|modificacion|reclamo|consulta|proactiva&texto&motivoCodigo
  // &motivoTipo&estado&kpi&resultado&canal&prioridad&responsableUsuarioId(0 = sin asignar)&origen&creadaDesde
  // &creadaHasta&cierreDesde&cierreHasta&incluirHistorico&pagina&tamanoPagina
  listarGestiones: async (params = {}) => getJson("/gestiones", params),
  // { gestion, timeline, otrasGestiones, totalOtrasGestiones, puedeGestionar, puedeTomar, puedeAsignar, ... }
  obtenerGestion: async (id) => getJson(`/gestiones/${id}`),
  // { cliente, terminales, gestionesAbiertas, ultimasCerradas, fechaCorteHistorico } of one alta (clienteId).
  obtenerContexto: async (clienteId) => getJson("/contexto", { clienteId }),
  // .xlsx of Finalizadas with the list filters (no page), incluirHistorico included. The server caps the rows and
  // says so in X-Ipc-Export-Total (rows matching the filters), X-Ipc-Export-Rows (rows written to the file) and
  // X-Ipc-Export-Truncated ("true" when it cut them).
  // Answers { blob, fileName (from Content-Disposition, or null), total (or null), rows (or null), truncated }.
  exportarFinalizadas: async (params = {}) => {
    const response = await get("/gestiones/exportar", params);
    if (!response.ok) return handleResponse(response);
    const header = (name) => response.headers?.get?.(name) ?? null;
    const count = (name) => {
      const value = Number.parseInt(header(name), 10);
      return Number.isNaN(value) ? null : value;
    };
    return {
      blob: await response.blob(),
      fileName: fileNameOf(header("Content-Disposition")),
      total: count("X-Ipc-Export-Total"),
      rows: count("X-Ipc-Export-Rows"),
      truncated: String(header("X-Ipc-Export-Truncated")).toLowerCase() === "true",
    };
  },
  // body: { clienteId, numeroTerminal, categoria, tipo, motivoCodigo, canal, fechaContacto, estado, motivoCierre,
  // fechaProximoContacto, prioridad, observacion, correlationId }
  crearGestion: async (body = {}) => postJson("/gestiones", body),
  // body: { accion, canal, resultadoContacto, fechaProximoContacto, prioridad, resultado, motivoCierre, observacion,
  // versionToken, correlationId }. A stale versionToken answers 409.
  ejecutarAccion: async (id, body = {}) => postJson(`/gestiones/${id}/acciones`, body),
  // Responsible commands (D25). correlationId is optional: one per attempt makes a retry idempotent (RN5); an
  // undefined value is not sent.
  tomar: async (id, { versionToken, correlationId } = {}) => postJson(`/gestiones/${id}/tomar`, { versionToken, correlationId }),
  // observacion is required when the gestión has another responsible (D25).
  asignar: async (id, { usuarioId, observacion, versionToken, correlationId } = {}) =>
    postJson(`/gestiones/${id}/asignar`, { usuarioId, observacion, versionToken, correlationId }),
  liberar: async (id, { observacion, versionToken, correlationId } = {}) =>
    postJson(`/gestiones/${id}/liberar`, { observacion, versionToken, correlationId }),
};
